import { Maybe, maybe } from '@passionware/monads';
import { dateConvert } from 'v5/platform/lang/dateConvert';

export type MixedCalendarDayInput = Maybe<string | Date>;

export type FormatMixedCalendarDayOptions = {
  dateOnly: boolean;
};

/** v1 event string fields produced from a calendar day range. */
export type EventDateRangeFields = {
  start_date: string;
  end_date: string;
  start_timestamp: string;
  end_timestamp: string;
};

/** Event / API string or Date → local Date for pickers (preserve wall clock). */
export function parseMixedCalendarDay(
  value: MixedCalendarDayInput
): Maybe<Date> {
  if (maybe.isAbsent(value)) {
    return undefined;
  }
  return typeof value === 'string'
    ? dateConvert.mixed.parse.preserveSystemClock(value)
    : value;
}

/** Local Date → event / API string (preserve wall clock). */
export function formatMixedCalendarDay(
  date: Date,
  options: FormatMixedCalendarDayOptions
): string {
  return dateConvert.mixed.format.preserveSystemClock(date, options);
}

/** v1 event date fields from a calendar range (Date or parseable values). */
export function eventFieldsFromDateRange(
  from: MixedCalendarDayInput,
  to: MixedCalendarDayInput
): EventDateRangeFields {
  const fromDate = parseMixedCalendarDay(from);
  const toDate = parseMixedCalendarDay(to);
  if (maybe.isAbsent(fromDate) || maybe.isAbsent(toDate)) {
    throw new Error(
      'eventFieldsFromDateRange requires valid from and to values'
    );
  }
  return {
    start_date: formatMixedCalendarDay(fromDate, { dateOnly: true }),
    end_date: formatMixedCalendarDay(toDate, { dateOnly: true }),
    start_timestamp: formatMixedCalendarDay(fromDate, { dateOnly: false }),
    end_timestamp: formatMixedCalendarDay(toDate, { dateOnly: false })
  };
}
